import UpgradeNeeded from "@components/upgrade-needed";
import { getPackPreview } from "@features/images/use-packs";
import {
  PaintBrushIcon,
  SparklesIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Heading, Text } from "@radix-ui/themes";
import { motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { onboardingPackName } from "../hooks/use-onboarding";
import { ActionCard } from "./ActionCard";

interface PreviewViewProps {
  imageUrl: string;
  selectedPack: string;
  onChangePack: () => void;
  onProfessional: () => void;
  onEdit: () => void;
  onTrain: () => void;
  onReset: () => void;
}

export const PreviewView = ({
  imageUrl,
  selectedPack,
  onChangePack,
  onProfessional,
  onEdit,
  onTrain,
  onReset,
}: PreviewViewProps) => {
  const { t } = useTranslation();
  const packPreview = getPackPreview(selectedPack);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center space-y-2">
        <Heading size="7">{t("onboarding.preview.title")}</Heading>
        <Text className="text-slate-600 dark:text-slate-400 block">
          {t("onboarding.preview.subtitle")}
        </Text>
      </div>

      {/* Image preview */}
      <div className="flex justify-center">
        <div className="relative">
          <img
            src={imageUrl}
            alt="Uploaded"
            className="max-h-[40vh] rounded-xl shadow-lg object-contain"
          />
          <button
            onClick={onReset}
            className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <XIcon />
          </button>
        </div>
      </div>

      {/* Action cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Professional Picture Card */}
        <div>
          <UpgradeNeeded minimumCredits={4}>
            <div className="p-5 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white transition-shadow hover:shadow-lg">
              {/* Icon */}
              <div className="text-white">
                <SparklesIcon className="w-6 h-6" />
              </div>

              {/* Pack selector */}
              <button
                className="mt-3 w-full flex items-center gap-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer group"
                onClick={onChangePack}
              >
                <div
                  className="w-12 h-12 rounded-lg bg-cover bg-center border-2 border-white/40 flex-shrink-0"
                  style={{ backgroundImage: `url(${packPreview})` }}
                />
                <div className="flex-1 text-left">
                  <Text className="text-xs text-white/70 block">
                    {t("onboarding.actions.professional.style")}
                  </Text>
                  <div className="flex items-center gap-1">
                    <Text className="text-sm font-medium text-white">
                      {onboardingPackName[selectedPack]}
                    </Text>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-white/70 group-hover:text-white transition-colors">
                  <Text className="text-xs">
                    {t("onboarding.actions.professional.change")}
                  </Text>
                  <ChevronDownIcon className="w-4 h-4" />
                </div>
              </button>

              {/* Action button */}
              <button
                className="mt-3 w-full py-3 px-4 rounded-lg bg-white/20 hover:bg-white/30 transition-colors font-semibold text-white"
                onClick={onProfessional}
              >
                {t("onboarding.actions.professional.title")}
              </button>
            </div>
          </UpgradeNeeded>
        </div>

        {/* Edit Picture Card */}

        <ActionCard
          icon={<PaintBrushIcon className="w-6 h-6" />}
          title={t("onboarding.actions.edit.title")}
          description={t("onboarding.actions.edit.description")}
          onClick={onEdit}
          className="hidden md:block"
        />

        {/* Train Model Card */}
        <ActionCard
          icon={<UserCircleIcon className="w-6 h-6" />}
          title={t("onboarding.actions.train.title")}
          description={t("onboarding.actions.train.description")}
          onClick={onTrain}
        />
      </div>
    </motion.div>
  );
};
